import React, { useEffect, useState } from 'react';
import { fetchPageBySlug, fetchCollection, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import GalleryModal from '../components/GalleryModal';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  // Gallery Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      try {
        const [page, galleries] = await Promise.all([
          fetchPageBySlug('om-rene'),
          fetchCollection('galleries')
        ]);
        setPageData(page);
        
        // Find the 'workshops' gallery
        const workshopsGallery = galleries.find(g => getLocalizedValue(g.slug) === 'workshops' || g.slug === 'workshops');
        setGallery(workshopsGallery);
      } catch (err) {
        console.error('Error loading about page data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Om René - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Mød René Vilsgaard, stærk senior freelanceprofil inden for ERP, Data Migration og Agile coaching.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.about.personTag}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.about.title}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.about.subtitle}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Main Text */}
          <div className="lg:col-span-7 space-y-8">
            <div 
              className="prose prose-navy max-w-none text-navy-900/80 leading-relaxed space-y-6 font-sans text-base sm:text-lg"
              dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
            />

            {/* Core Values / Approach */}
            <div className="pt-8 border-t border-bluegray space-y-8">
              <h2 className="font-serif text-2xl font-medium text-navy-900">{t.about.compassTitle}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 text-petrol font-bold uppercase tracking-wider text-xs">
                    <i className="fa-solid fa-heart-pulse"></i>
                    <span>{t.about.safetyTitle}</span>
                  </div>
                  <p className="text-sm text-charcoal/70 leading-relaxed">
                    {t.about.safetyText}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 text-petrol font-bold uppercase tracking-wider text-xs">
                    <i className="fa-solid fa-arrows-repeat"></i>
                    <span>{t.about.cadenceTitle}</span>
                  </div>
                  <p className="text-sm text-charcoal/70 leading-relaxed">
                    {t.about.cadenceText}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 text-petrol font-bold uppercase tracking-wider text-xs">
                    <i className="fa-solid fa-bridge"></i>
                    <span>{t.about.bridgeTitle}</span>
                  </div>
                  <p className="text-sm text-charcoal/70 leading-relaxed">
                    {t.about.bridgeText}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 text-petrol font-bold uppercase tracking-wider text-xs">
                    <i className="fa-solid fa-hand-holding-seedling"></i>
                    <span>{t.about.pragmaticTitle}</span>
                  </div>
                  <p className="text-sm text-charcoal/70 leading-relaxed">
                    {t.about.pragmaticText}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar & LinkedIn */}
          <div className="lg:col-span-5 space-y-8">
            {/* Profile Card */}
            <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 shadow-xl space-y-6">
              <div className="relative rounded-none overflow-hidden shadow-inner border border-bluegray/40">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/8297297f-eccf-4eba-a924-6f973a63cdc4/imgi-2-rvi-profilbillede.png"
                  alt="René Vilsgaard"
                  className="w-full h-64 object-cover rounded-none"
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-serif text-xl font-bold text-navy-900">René Vilsgaard</h3>
                <p className="text-xs text-petrol uppercase tracking-widest font-bold">// {t.about.profileTag}</p>
              </div>
              <p className="text-sm text-charcoal/80 leading-relaxed font-sans italic">
                {t.about.profileQuote}
              </p>
              <div className="pt-4 border-t border-bluegray/60 space-y-3 text-sm text-charcoal/80">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-petrol w-5"></i>
                  <a href="tel:30655509" className="hover:text-petrol font-semibold">30 65 55 09</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-petrol w-5"></i>
                  <a href="mailto:rvi@insightly.dk" className="hover:text-petrol font-semibold break-all">rvi@insightly.dk</a>
                </div>
                <div className="flex items-start gap-3 pt-2 border-t border-bluegray/40">
                  <i className="fa-solid fa-user-tie text-petrol w-5 mt-0.5"></i>
                  <div>
                    <span className="block text-xs font-bold text-navy-900 uppercase tracking-wider">Senior Freelance ERP Specialist</span>
                    <a 
                      href="https://psykoterapeutforeningen.dk" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-petrol hover:underline inline-flex items-center gap-1 mt-0.5"
                    >
                      <span>{t.about.member}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* LinkedIn CTA Block */}
            <div className="bg-navy-900 text-white rounded-none p-6 sm:p-8 shadow-xl space-y-4">
              <div className="flex items-center gap-3">
                <i className="fa-brands fa-linkedin text-3xl text-white"></i>
                <h3 className="font-serif text-lg font-bold text-white">{t.about.linkedinTitle}</h3>
              </div>
              <p className="text-sm text-white/90 leading-relaxed">
                {t.about.linkedinText}
              </p>
              <a
                href="https://dk.linkedin.com/in/rene-vilsgaard/da"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-petrol hover:bg-petrol-dark text-white py-3.5 rounded-none text-center font-bold uppercase tracking-wider text-xs transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
              >
                <span>{t.about.linkedinConnect}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-white"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Dynamic Gallery Section */}
        {gallery && gallery.images && gallery.images.length > 0 && (
          <div className="mt-24 pt-12 border-t border-bluegray space-y-8">
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.about.visualTag}</span>
              <h2 className="text-3xl font-serif font-medium text-navy-900 mt-2">
                {getLocalizedValue(gallery.title, language)}
              </h2>
              <p className="text-sm text-charcoal/60 mt-2 font-sans">
                {getLocalizedValue(gallery.description, language)} {t.about.visualClick}
              </p>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {gallery.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => handleImageClick(idx)}
                  className="relative rounded-none overflow-hidden shadow-md hover:shadow-xl border border-bluegray cursor-pointer group aspect-video"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`Galleri ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 rounded-none"
                  />
                  <div className="absolute inset-0 bg-navy-950/0 group-hover:bg-navy-950/20 transition-colors duration-300 flex items-center justify-center">
                    <i className="fa-light fa-magnifying-glass-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                  </div>
                </div>
              ))}
            </div>

            {/* Gallery Modal */}
            <GalleryModal
              isOpen={isModalOpen}
              images={gallery.images}
              currentIndex={selectedImageIndex}
              onClose={() => setIsModalOpen(false)}
              onPrev={() => setSelectedImageIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1))}
              onNext={() => setSelectedImageIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1))}
            />
          </div>
        )}
      </div>
    </div>
  );
}
